import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { reelId, embedUrl } from "@/components/ReelsModal";

export const InstagramStrip = ({ reels, profile, brands = [] }) => {
  const scroller = useRef(null);
  const autoPlaying = useRef(true);
  const stopAuto = () => {
    autoPlaying.current = false;
    if (scroller.current) scroller.current.style.scrollSnapType = "";
  };
  const scroll = (dir) => {
    stopAuto();
    scroller.current?.scrollBy({ left: dir * 560, behavior: "smooth" });
  };

  const { add, setIsOpen } = useCart();
  const [allProducts, setAllProducts] = useState([]);

  const valid = (reels || [])
    .map((r) => (typeof r === "string" ? { url: r, product_id: "" } : r))
    .map((r) => ({ ...r, id: reelId(r.url) }))
    .filter((r) => r.id);

  // Gentle auto-glide until the shopper interacts; ping-pongs at the ends.
  // scroll-snap is suspended while gliding (mandatory snap pulls micro-scrolls back to tile boundaries).
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    let dir = 1;
    let raf;
    if (autoPlaying.current) el.style.scrollSnapType = "none";
    const step = () => {
      if (autoPlaying.current && el.scrollWidth > el.clientWidth) {
        el.scrollLeft += 0.5 * dir;
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) dir = -1;
        else if (el.scrollLeft <= 0) dir = 1;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    // Clicks/taps anywhere stop the glide; window blur catches taps into the cross-origin Instagram iframes
    window.addEventListener("pointerdown", stopAuto, { once: true });
    window.addEventListener("blur", stopAuto, { once: true });
    el.addEventListener("touchstart", stopAuto, { once: true });
    el.addEventListener("wheel", stopAuto, { once: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointerdown", stopAuto);
      window.removeEventListener("blur", stopAuto);
      el.removeEventListener("touchstart", stopAuto);
      el.removeEventListener("wheel", stopAuto);
      el.style.scrollSnapType = "";
    };
  }, [valid.length, brands.length]);

  useEffect(() => {
    api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {});
  }, []);

  // Count a view once per reel when a tile is at least half visible
  useEffect(() => {
    const els = scroller.current?.querySelectorAll("[data-reel]");
    if (!els?.length) return;
    const seen = new Set();
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting && !seen.has(en.target.dataset.reel)) {
          seen.add(en.target.dataset.reel);
          api.post("/analytics/reel-view", { reel_id: en.target.dataset.reel, surface: "home" }).catch(() => {});
        }
      });
    }, { threshold: 0.5 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [valid.length]);

  if (!valid.length && !brands.length) return null;

  const byId = Object.fromEntries(allProducts.map((p) => [p.id, p]));
  const brandOf = (slug) => brands.find((b) => b.slug === slug);
  const brandProduct = (slug) => allProducts.find((p) => p.brand_slug === slug);

  // One tile per reel (brand from its tagged product), then a placeholder tile for every brand without a reel
  const reelTiles = valid.map((r) => {
    const product = r.product_id ? byId[r.product_id] : null;
    return { type: "reel", reel: r, product, brand: product ? brandOf(product.brand_slug) : null };
  });
  const reelBrands = new Set(reelTiles.map((t) => t.brand?.slug).filter(Boolean));
  const placeholderTiles = brands
    .filter((b) => !reelBrands.has(b.slug))
    .map((b) => ({ type: "brand", brand: b, product: brandProduct(b.slug) }));

  const tiles = [...reelTiles, ...placeholderTiles];
  if (!tiles.length) return null;

  const quickAdd = (p, reelKey) => {
    add(p, 1);
    setIsOpen(true);
    toast.success(`${p.name} added to your bag`);
    api.post("/analytics/reel-add", { reel_id: reelKey, product_id: p.id, surface: "home" }).catch(() => {});
  };

  const Tag = ({ product, reelKey }) => (
    <div className="absolute bottom-3 left-3 right-3 z-10 bg-white/90 backdrop-blur-md px-4 py-3 rounded-lg flex items-center gap-3" data-testid={`reel-shop-tag-${product.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-[9px] uppercase tracking-[0.2em] text-gold">Featured here</p>
        <p className="text-xs text-zinc-900 leading-snug line-clamp-1 mt-0.5">{product.name}</p>
      </div>
      <button
        onClick={() => quickAdd(product, reelKey)}
        data-testid={`reel-shop-add-${product.id}`}
        className="shrink-0 bg-gold text-white text-[9px] uppercase tracking-[0.2em] px-3.5 py-2 hover:bg-champagne transition-colors duration-300"
      >
        {inr(product.price)} · Add
      </button>
    </div>
  );

  return (
    <section className="py-20 bg-white border-b border-zinc-200" data-testid="instagram-strip">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.5em] text-gold mb-3 flex items-center gap-2">
              <Instagram size={13} /> From Instagram
            </p>
            <h2 className="font-serif font-medium text-3xl sm:text-4xl text-zinc-900 tracking-tight">
              Fresh from the <em className="text-gold">feed</em>
            </h2>
          </div>
          {profile && (
            <a
              href={profile}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-btn"
              className="group hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-gold transition-colors duration-300"
            >
              Follow us
            </a>
          )}
          <div className="hidden md:flex gap-2">
            <button onClick={() => scroll(-1)} data-testid="instagram-prev" aria-label="Previous" className="p-3 border border-zinc-200 text-zinc-500 hover:border-gold hover:text-gold transition-colors duration-300">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => scroll(1)} data-testid="instagram-next" aria-label="Next" className="p-3 border border-zinc-200 text-zinc-500 hover:border-gold hover:text-gold transition-colors duration-300">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div
          ref={scroller}
          data-testid="instagram-scroller"
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tiles.map((t, i) =>
            t.type === "reel" ? (
              <div key={t.reel.id} data-reel={t.reel.id} className="relative w-64 md:w-72 shrink-0 snap-start rounded-xl overflow-hidden border border-zinc-200 bg-zinc-50 aspect-[9/14]" data-testid={`instagram-reel-${t.reel.id}`}>
                {t.brand && (
                  <span className="absolute top-3 left-3 z-10 text-[9px] uppercase tracking-[0.25em] bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-zinc-700" data-testid={`reel-brand-${t.reel.id}`}>
                    {t.brand.name}
                  </span>
                )}
                <iframe
                  src={embedUrl(t.reel.url)}
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allow="encrypted-media"
                  loading="lazy"
                  title="Instagram reel"
                />
                {t.product && <Tag product={t.product} reelKey={t.reel.id} />}
              </div>
            ) : (
              <div key={t.brand.slug} className="relative w-64 md:w-72 shrink-0 snap-start rounded-xl overflow-hidden border border-zinc-200 bg-[#faf7f7] aspect-[9/14]" data-testid={`instagram-brand-${t.brand.slug}`}>
                {t.brand.image && (
                  <img src={t.brand.image} alt={t.brand.name} className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
                )}
                <div className="absolute inset-x-0 top-0 p-5 flex flex-col items-start gap-2">
                  <span className="text-[9px] uppercase tracking-[0.25em] bg-gold text-white px-3 py-1.5 rounded-full">{t.brand.name}</span>
                  <p className="text-xs text-zinc-500 mt-2 leading-relaxed">Reels landing here soon — follow along.</p>
                  {t.brand.instagram_profile && (
                    <a
                      href={t.brand.instagram_profile}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`brand-ig-${t.brand.slug}`}
                      className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gold hover:text-champagne transition-colors duration-300"
                    >
                      <Instagram size={13} /> @{t.brand.instagram_profile.replace(/\/$/, "").split("/").pop()}
                    </a>
                  )}
                </div>
                {t.product && <Tag product={t.product} reelKey={`brand-${t.brand.slug}`} />}
              </div>
            )
          )}
          {profile && (
            <a
              href={profile}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-tile"
              className="w-64 md:w-72 shrink-0 snap-start rounded-xl border border-dashed border-gold/40 bg-pink-50/40 flex flex-col items-center justify-center gap-3 aspect-[9/14] group"
            >
              <Instagram size={22} className="text-gold" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Follow for more</span>
            </a>
          )}
        </div>
      </div>
    </section>
  );
};
